/**
 * Magic-item eligibility for the character item picker.
 *
 * Replaces the old substring test (`restriction.includes(unit.name) ||
 * restriction.includes(unit.troop_type)`) which was backwards for troop-type
 * restrictions: it compared the restriction text against the *full* troop_type
 * string (e.g. "Heavy infantry (character)"), so every troop-type restriction
 * matched nobody and the item was hidden from everyone. See
 * db/scripts/audit/check_magic_item_restrictions.py for the audit that quantified
 * this (56 of 84 restricted items were unselectable).
 *
 * Resolution order:
 *  1. No restriction -> eligible.
 *  2. Army-of-Infamy items need army-composition gating we don't have here, so we
 *     preserve the legacy substring behaviour rather than surfacing illegal picks.
 *     (Proper composition gating is a follow-up.)
 *  3. Troop-type restriction -> eligible if the unit's troop_type contains a named
 *     troop keyword.
 *  4. Name whitelist -> if any unit in the faction is named in the restriction,
 *     treat it as a name gate and admit only named units.
 *  5. Otherwise the restriction is a soft/cap rule (armour requirement, "0-1 per",
 *     single use, equipment) that isn't an eligibility gate -> show it rather than
 *     hide a legal item. Points caps and equipment are enforced elsewhere.
 */

export interface EligibilityUnit {
  name: string;
  troop_type: string;
}

const TROOP_KEYWORDS = ['infantry', 'cavalry', 'monster', 'chariot', 'monstrous'];

export function isMagicItemEligible(
  restriction: string | undefined,
  unit: EligibilityUnit,
  allUnits: readonly EligibilityUnit[],
): boolean {
  const r = (restriction ?? '').trim();
  if (!r) return true;

  const rl = r.toLowerCase();
  const nameL = unit.name.toLowerCase();
  const troopL = unit.troop_type.toLowerCase();

  // 1. Army of Infamy: preserve legacy behaviour (needs composition gating).
  if (rl.includes('army of infamy')) {
    return rl.includes(nameL) || rl.includes(troopL);
  }

  // 2. Troop-type restriction.
  const troopKw = TROOP_KEYWORDS.filter((kw) => rl.includes(kw));
  if (troopKw.length > 0) {
    return troopKw.some((kw) => troopL.includes(kw));
  }

  // 3. Name whitelist.
  const nameGate = allUnits.some((u) => u.name && rl.includes(u.name.toLowerCase()));
  if (nameGate) {
    return rl.includes(nameL);
  }

  // 4. Soft / cap restriction: not an eligibility gate.
  return true;
}

// ---------------------------------------------------------------------------
// Magic armour access (rulebook p.341)
// ---------------------------------------------------------------------------

interface ArmouryUnit {
  equipment: string[] | Record<string, string[]>;
  options?: { description: string; choices?: { description: string }[] }[];
}

const ARMOUR_RE = /\b(?:light|heavy|full plate|chaos|gromril|ithilmar)\s+armou?r\b|full plate|scaly skin/i;
const SHIELD_RE = /\bshields?\b/i;

/**
 * What kinds of mundane protection this unit is equipped with or may buy.
 * "Only a model that is equipped with (or that has the option to be equipped
 * with) armour, a shield or barding may purchase a magical equivalent"
 * (rulebook p.341).
 */
export function unitArmouryAccess(
  unit: ArmouryUnit,
  extraEquipment: string[] = [],
): { armour: boolean; shield: boolean; barding: boolean } {
  const texts: string[] = [...extraEquipment];
  const equip = unit.equipment;
  if (Array.isArray(equip)) texts.push(...equip);
  else if (equip && typeof equip === 'object') for (const v of Object.values(equip)) texts.push(...v);
  for (const o of unit.options ?? []) {
    texts.push(o.description);
    for (const c of o.choices ?? []) texts.push(c.description);
  }
  return {
    armour: texts.some((t) => ARMOUR_RE.test(t)),
    shield: texts.some((t) => SHIELD_RE.test(t)),
    barding: texts.some((t) => /barding/i.test(t)),
  };
}

interface ArmourItem {
  category?: string;
  is_shield?: boolean;
  armour_profile?: { armour_value?: string; type?: string; ignores_armour_requirement?: boolean };
}

/**
 * Whether this model may buy this magic armour item: a magic suit needs
 * mundane armour access, a magic shield needs shield access, a worn-with-
 * other-armour piece (helm, pelt) needs any of the three - unless the item
 * itself waives the requirement ("may be purchased and worn by a Wizard,
 * even if they do not have the option to be equipped with armour").
 *
 * `extraEquipment` carries selection-dependent access: a selected honour's
 * free equipment ("may be equipped with full plate armour for no additional
 * points" - Blood of Caledor) opens the armoury like any bought option.
 */
export function magicArmourAllowed(item: ArmourItem, unit: ArmouryUnit, extraEquipment: string[] = []): boolean {
  if (item.category !== 'magic_armour') return true;
  const ap = item.armour_profile;
  if (ap?.ignores_armour_requirement) return true;
  const access = unitArmouryAccess(unit, extraEquipment);
  if (ap?.type === 'shield' || item.is_shield || /^shield$/i.test(String(ap?.armour_value ?? ''))) {
    return access.shield;
  }
  if (/^\+/.test(String(ap?.armour_value ?? '').trim())) {
    return access.armour || access.shield || access.barding;
  }
  // A suit (or an unprofiled magic armour item) replaces mundane armour
  return access.armour;
}

/**
 * The Battle March magic items live in the shared pool alongside the common
 * items, but the General's Companion is explicit that they are "created
 * especially for games of Battle March and, as such, should only be included in
 * muster lists written for games of Battle March". They carry
 * `source: 'battle_march'` so the pickers can hide them from every other list.
 */
export function isBattleMarchItem(item: { source?: string }): boolean {
  return item.source === 'battle_march';
}

/** Drop the Battle March items unless this army is a Battle March list. */
export function itemsForFormat<T extends { source?: string }>(
  items: readonly T[],
  isBattleMarch: boolean,
): T[] {
  return isBattleMarch ? [...items] : items.filter((i) => !isBattleMarchItem(i));
}

/**
 * Origin-based item access, two distinct semantics:
 *
 *  - intrinsicOrigin (a unit BORROWED from another book - a Black Orc Boss in
 *    a Chaos Dwarf army, every Hordes of Chaos model): the model buys from its
 *    PARENT book's items plus the Commons, and nothing else - not even the
 *    borrowing faction's own list.
 *  - unlockedOrigin (a Renegade Crowns Infamous Origin): the honour ADDS the
 *    parent list on top of everything the model could already buy.
 *
 * Items carry `origin` for a parent-tagged copy; runtime-merged rulebook
 * Commons carry `is_common`; a faction's own items carry neither.
 */
export function itemsForOrigin<T extends { origin?: string; is_common?: boolean }>(
  items: readonly T[],
  intrinsicOrigin: string | undefined,
  unlockedOrigin?: string,
): T[] {
  if (intrinsicOrigin) {
    return items.filter((i) => i.is_common || i.origin === intrinsicOrigin);
  }
  // A single-list faction tags nothing, so there is nothing to segregate.
  if (!items.some((i) => i.origin)) return [...items];
  return items.filter((i) => !i.origin || i.origin === unlockedOrigin);
}
